import { ApiRequestError } from "@/lib/api";

/**
 * Friendly error panel. Never renders a stack trace; maps known cases to plain
 * language and offers a retry. (CLAUDE.md §2.2: no raw 401 blobs.)
 */
export function ErrorState({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry?: () => void;
}) {
  const message = friendlyMessage(error);

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <p className="text-sm font-medium text-destructive">Something went wrong</p>
      <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-1 rounded-md border px-3 py-1.5 text-sm font-medium transition hover:bg-accent"
        >
          Try again
        </button>
      )}
    </div>
  );
}

function friendlyMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.status === 401) {
      return "Your session expired. Please sign in again.";
    }
    if (error.status === 403) {
      // Soft-deactivated account (sql/020): surface the server's specific
      // message instead of the generic gate text — the user can still complete
      // Supabase sign-in (lockout is API-middleware-side), so this is the only
      // cue they get.
      if (error.body?.error === "account_deactivated") {
        return (
          error.body.message ??
          "This account has been deactivated. Contact your administrator."
        );
      }
      // e.g. AI reveal before self-score submit — the calling screen should
      // normally render its own gate UI rather than fall through to here.
      return "You don't have access to this yet.";
    }
    if (error.status === 404) return "We couldn't find that call.";
    if (error.status >= 500) {
      return "The server had a problem. Please try again in a moment.";
    }
  }
  return "Please try again. If this keeps happening, contact your manager.";
}
